import { CartUpdateEvent } from '@theme/events';

/*
 * Cart extras (replaces the Monster Upsell app):
 * - <free-shipping-bar>: progress towards free express shipping, with a short confetti burst on unlock
 * - <cart-recs>: "Frequently bought together" with quick add
 * - colour/variant switch on cart lines
 */

/** Formats cents like the store's money format, using a rendered sample of 1234.56. */
function formatMoney(cents, sample) {
  const match = sample.match(/1(\D?)234(\D?)56/);
  const value = cents / 100;
  if (!match) return value.toFixed(2);
  const [whole, fraction] = value.toFixed(2).split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, match[1]);
  return sample.replace(match[0], `${grouped}${match[2] || '.'}${fraction}`);
}

function sectionIds() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
  });
  return [...ids];
}

async function postJSON(url, body) {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await response.json();
  if (!response.ok || data.status) throw new Error(data.description || data.message || 'Cart error');
  return data;
}

/** Re-renders the cart drawer / cart page and the header count after a change we made ourselves. */
async function refreshCart(sections) {
  const cart = await (await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`)).json();
  document.dispatchEvent(
    new CartUpdateEvent(cart, 'eagle-cart', {
      itemCount: cart.item_count,
      source: 'eagle-cart',
      sections,
    })
  );
}

function confetti() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  const canvas = document.createElement('canvas');
  canvas.className = 'free-shipping-confetti';
  const dialog = document.querySelector('.cart-drawer__dialog[open]');
  (dialog || document.body).append(canvas);
  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas.remove();
  const dpr = window.devicePixelRatio || 1;
  const width = (canvas.width = window.innerWidth * dpr);
  const height = (canvas.height = window.innerHeight * dpr);
  const bar = document.querySelector('free-shipping-bar')?.getBoundingClientRect();
  const originX = bar ? (bar.left + bar.width / 2) * dpr : width / 2;
  const originY = bar ? bar.top * dpr : height / 3;
  const colors = ['#b68d40', '#d9bf8c', '#2d5a27', '#162543', '#f3e7cf'];
  const pieces = Array.from({ length: 90 }, () => ({
    x: originX,
    y: originY,
    vx: (Math.random() - 0.5) * 14 * dpr,
    vy: (-Math.random() * 11 - 4) * dpr,
    size: (Math.random() * 5 + 4) * dpr,
    rotation: Math.random() * Math.PI,
    spin: (Math.random() - 0.5) * 0.3,
    color: colors[Math.floor(Math.random() * colors.length)],
  }));
  const start = performance.now();
  const frame = (now) => {
    const t = now - start;
    ctx.clearRect(0, 0, width, height);
    ctx.globalAlpha = Math.max(0, 1 - t / 1800);
    for (const p of pieces) {
      p.vy += 0.35 * dpr;
      p.vx *= 0.985;
      p.x += p.vx;
      p.y += p.vy;
      p.rotation += p.spin;
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rotation);
      ctx.fillStyle = p.color;
      ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2);
      ctx.restore();
    }
    if (t < 1800) requestAnimationFrame(frame);
    else canvas.remove();
  };
  requestAnimationFrame(frame);
}

class FreeShippingBar extends HTMLElement {
  static observedAttributes = ['data-total'];

  connectedCallback() {
    // Inserted after page load (first item added from the drawer) counts as a live change.
    this.update(document.readyState === 'complete' && performance.now() > 4000);
  }

  attributeChangedCallback(_name, oldValue, newValue) {
    if (this.isConnected && oldValue !== null && oldValue !== newValue) this.update(true);
  }

  update(animate) {
    const rate = parseFloat(window.Shopify?.currency?.rate || '1') || 1;
    const total = parseInt(this.dataset.total || '0', 10);
    const goal = Math.round(parseInt(this.dataset.goal || '0', 10) * rate);
    if (!goal) return;
    const progress = Math.min(total / goal, 1);
    const complete = progress >= 1;
    const text = this.querySelector('.free-shipping-bar__text');
    const sample = this.dataset.moneySample || '';

    if (text) {
      if (complete) {
        text.textContent = `🎉 ${this.dataset.after}`;
      } else {
        const [before, after] = (this.dataset.before || '').split('[amount]');
        const amount = document.createElement('strong');
        // Same class as Liquid's money output, so the storefront currency converter picks it up.
        amount.className = 'money';
        amount.textContent = formatMoney(goal - total, sample);
        text.replaceChildren(before || '', amount, after || '');
      }
    }

    // State lives on the children: the cart morph resets this element's own attributes.
    this.querySelector('.free-shipping-bar__inner')?.classList.toggle('is-complete', complete);
    this.querySelector('.free-shipping-bar__fill')?.style.setProperty('--progress', String(progress));

    let previous = null;
    try {
      previous = sessionStorage.getItem('eg-free-express');
      sessionStorage.setItem('eg-free-express', complete ? 'complete' : 'open');
    } catch (e) {}
    if (complete && animate && previous !== 'complete') confetti();
  }
}

class CartRecs extends HTMLElement {
  static observedAttributes = ['data-product-id', 'data-in-cart'];

  #requestId = 0;

  connectedCallback() {
    this.load();
    this.addEventListener('click', this.#onClick);
    this.addEventListener('change', this.#onChange);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('change', this.#onChange);
  }

  attributeChangedCallback(_name, oldValue, newValue) {
    if (this.isConnected && oldValue !== null && oldValue !== newValue) this.load();
  }

  async load() {
    const productId = this.dataset.productId;
    const list = this.querySelector('.cart-recs__list');
    if (!productId || !list) return;
    const requestId = ++this.#requestId;
    const inCart = new Set((this.dataset.inCart || '').split(',').filter(Boolean));

    try {
      const url = `${this.dataset.url}?product_id=${productId}&limit=8&section_id=eagle-cart-recs`;
      const html = await (await fetch(url)).text();
      if (requestId !== this.#requestId) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const items = [...doc.querySelectorAll('.cart-recs__item')]
        .filter((item) => item instanceof HTMLElement && !inCart.has(item.dataset.productId || ''))
        .slice(0, 2);
      list.replaceChildren(...items.map((item) => document.importNode(item, true)));
    } catch (e) {
      list.replaceChildren();
    }
  }

  #onChange = (event) => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement)) return;
    const price = select.selectedOptions[0]?.dataset.price;
    const target = select.closest('.cart-recs__item')?.querySelector('[data-rec-price]');
    if (price && target) target.textContent = price;
  };

  #onClick = async (event) => {
    const button = event.target instanceof Element ? event.target.closest('.cart-recs__add') : null;
    if (!(button instanceof HTMLButtonElement)) return;
    const input = button.closest('.cart-recs__item')?.querySelector('.cart-recs__select');
    if (!(input instanceof HTMLSelectElement || input instanceof HTMLInputElement)) return;

    button.setAttribute('aria-busy', 'true');
    try {
      const sections = sectionIds();
      const data = await postJSON(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        items: [{ id: Number(input.value), quantity: 1 }],
        sections: sections.join(','),
        sections_url: window.location.pathname,
      });
      await refreshCart(data.sections);
    } catch (e) {
      console.error(e);
    } finally {
      button.removeAttribute('aria-busy');
    }
  };
}

/** Swaps a cart line to another variant, keeping its quantity and properties (e.g. personalization). */
document.addEventListener('change', async (event) => {
  const select = event.target;
  if (!(select instanceof HTMLSelectElement) || !select.classList.contains('cart-items__variant-select')) return;

  const { lineKey, quantity } = select.dataset;
  let properties = {};
  try {
    const parsed = JSON.parse(select.dataset.properties || '{}');
    // Liquid serialises line properties as [[name, value], …].
    properties = Array.isArray(parsed) ? Object.fromEntries(parsed) : parsed || {};
  } catch (e) {}
  const newId = Number(select.value);
  if (!lineKey || !newId) return;

  const component = select.closest('cart-items-component');
  component?.classList.add('cart-items-disabled');
  const root = window.Shopify?.routes?.root || '/';

  try {
    await postJSON(`${root}cart/add.js`, {
      items: [{ id: newId, quantity: Number(quantity) || 1, properties }],
    });
    const sections = sectionIds();
    const data = await postJSON(`${root}cart/change.js`, {
      id: lineKey,
      quantity: 0,
      sections: sections.join(','),
      sections_url: window.location.pathname,
    });
    await refreshCart(data.sections);
  } catch (e) {
    console.error(e);
    await refreshCart();
  } finally {
    component?.classList.remove('cart-items-disabled');
  }
});

/*
 * Shopify's "Shop Pay emphasis" stacks every wallet button full width, which buries Checkout.
 * Without it the wallets fall back to Shopify's own compact grid (2 per row in the cart).
 */
function unstackWallets(root = document) {
  root.querySelectorAll('shopify-accelerated-checkout-cart[shop-pay-emphasis]').forEach((el) => {
    el.removeAttribute('shop-pay-emphasis');
  });
}
unstackWallets();
new MutationObserver((mutations) => {
  for (const m of mutations) {
    if (m.type === 'attributes' || m.addedNodes.length) return unstackWallets();
  }
}).observe(document.documentElement, { subtree: true, childList: true, attributes: true, attributeFilter: ['shop-pay-emphasis'] });

if (!customElements.get('free-shipping-bar')) customElements.define('free-shipping-bar', FreeShippingBar);
if (!customElements.get('cart-recs')) customElements.define('cart-recs', CartRecs);
